import { MdAdd, MdDelete } from 'react-icons/md'
import { useClientLabel } from '@/lib/hooks/use-client-label'

interface Property {
    name: string
    description: string
}

interface PropertiesFieldsProps {
    properties: Record<string, any>
    onChange: (properties: Record<string, any>) => void
    prefix?: string
    buttonText?: string
    mode?: 'properties' | 'keyValue'
}

export default function PropertiesFields({
    properties,
    onChange,
    prefix = '',
    buttonText = 'Adicionar Propriedade',
    mode = 'properties',
}: PropertiesFieldsProps) {
    const isKeyValueMode = mode === 'keyValue'
    const { clientLabel } = useClientLabel()

    // Converter properties de objeto para array de Property
    const propertiesArray: Property[] = Object.entries(properties || {}).map(
        ([name, data]) => {
            if (isKeyValueMode) {
                if (typeof data === 'string') {
                    return { name, description: data }
                }
                return {
                    name,
                    description: data?.value || data?.description || '',
                }
            }
            if (typeof data === 'string') {
                return { name, description: data }
            }
            return {
                name,
                description: data?.description || '',
            }
        }
    )

    const hasEntries = propertiesArray.length > 0

    const addProperty = () => {
        const newProperties = { ...properties }
        const newName = ``
        if (isKeyValueMode) {
            newProperties[newName] = { value: '' }
        } else {
            newProperties[newName] = { description: '' }
        }
        onChange(newProperties)
    }

    const updateProperty = (
        oldName: string,
        newName: string,
        description: string
    ) => {
        const newProperties = { ...properties }
        if (oldName !== newName) {
            delete newProperties[oldName]
        }
        if (isKeyValueMode) {
            newProperties[newName] = { value: description }
        } else {
            newProperties[newName] = { description }
        }
        onChange(newProperties)
    }

    const removeProperty = (name: string) => {
        const newProperties = { ...properties }
        delete newProperties[name]
        onChange(newProperties)
    }

    const getFieldId = (index: number, field: 'name' | 'description') => {
        return prefix
            ? `${prefix}_property_${index}_${field}`
            : `property_${index}_${field}`
    }

    return (
        <div className="space-y-4">
            {propertiesArray.map((property, index) => (
                <div
                    key={`${property.name}-${index}`}
                    className="bg-base-200/50 rounded-lg p-4 border border-base-300/50"
                >
                    <div className="flex items-start justify-between gap-2 mb-3">
                        <h4 className="text-sm font-semibold text-base-content">
                            {isKeyValueMode
                                ? `Item #${index + 1}`
                                : `Propriedade #${index + 1}`}
                        </h4>
                        <button
                            type="button"
                            onClick={() => removeProperty(property.name)}
                            className="btn btn-error btn-xs btn-square"
                            title="Remover propriedade"
                        >
                            <MdDelete className="text-base" />
                        </button>
                    </div>

                    <div className="space-y-3">
                        <div className="form-control w-full">
                            <label className="label py-1">
                                <span className="label-text text-xs">
                                    Nome <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                type="text"
                                id={getFieldId(index, 'name')}
                                className="input input-bordered w-full input-sm"
                                placeholder={
                                    isKeyValueMode
                                        ? prefix === 'params'
                                            ? 'Ex: page, limit, filter, sort'
                                            : 'Ex: Content-Type, Accept, Authorization'
                                        : 'Ex: cpf, email, telefone, pedido_id'
                                }
                                value={property.name}
                                onChange={e =>
                                    updateProperty(
                                        property.name,
                                        e.target.value,
                                        property.description
                                    )
                                }
                                required
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label py-1">
                                <span className="label-text text-xs">
                                    {isKeyValueMode ? 'Valor' : 'Descrição'}{' '}
                                    <span className="text-error">*</span>
                                </span>
                                {!isKeyValueMode && (
                                    <span className="label-text-alt">
                                        Sobre esta propriedade
                                    </span>
                                )}
                            </label>
                            {isKeyValueMode ? (
                                <input
                                    type="text"
                                    id={getFieldId(index, 'description')}
                                    className="input input-bordered w-full input-sm"
                                    placeholder={
                                        prefix === 'params'
                                            ? 'Ex: 1, 10, active, desc'
                                            : 'Ex: application/json'
                                    }
                                    value={property.description}
                                    onChange={e =>
                                        updateProperty(
                                            property.name,
                                            property.name,
                                            e.target.value
                                        )
                                    }
                                    required
                                />
                            ) : (
                                <textarea
                                    id={getFieldId(index, 'description')}
                                    className="textarea textarea-bordered w-full textarea-sm"
                                    placeholder="Ex: Por favor, informe seu CPF para consultar o pedido"
                                    value={property.description}
                                    onChange={e =>
                                        updateProperty(
                                            property.name,
                                            property.name,
                                            e.target.value
                                        )
                                    }
                                    rows={2}
                                    required
                                />
                            )}
                        </div>
                    </div>
                </div>
            ))}

            <button
                type="button"
                onClick={addProperty}
                className="btn btn-outline btn-sm gap-2"
            >
                <MdAdd className="text-lg" />
                {buttonText}
            </button>

            {!hasEntries && (
                <div className="text-center py-4 text-sm text-base-content/60 bg-base-200/30 rounded-lg border border-dashed border-base-300/50">
                    {isKeyValueMode
                        ? 'Nenhum item adicionado.'
                        : 'Nenhuma propriedade adicionada.'}
                    <br />
                    {!isKeyValueMode && (
                        <span className="text-xs">
                            As propriedades são dados que a IA solicitará ao
                            {clientLabel} via WhatsApp
                        </span>
                    )}
                </div>
            )}
        </div>
    )
}
